import { createClient } from "@b3dotfun/b3-api";
import rest from "@feathersjs/rest-client";
import socketio from "@feathersjs/socketio-client";
import io from "socket.io-client";
import { authenticate as authenticateB3, getB3ApiUrl, clientOptions } from "../app.shared.js";
// Global state to track which client type is active
let currentClientType = "rest";
let currentClient = null;
// Socket client instance
let socketClient = null;
let socketInstance = null;
let socketClientUrl = null;
// REST client instance
let restClient = null;
let restClientUrl = null;
/**
 * Creates a socket client
 */
function createSocketClient() {
    const url = getB3ApiUrl();
    if (!socketClient || socketClientUrl !== url) {
        socketInstance = io(url, { transports: ["websocket"] });
        socketClient = createClient(socketio(socketInstance), clientOptions);
        socketClientUrl = url;
    }
    return socketClient;
}
/**
 * Creates a REST client
 */
function resolveFetch() {
    const f = globalThis.fetch;
    if (typeof f === "function")
        return f.bind(globalThis);
    // lazy-require for Node environments
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require("cross-fetch").fetch;
}
function createRestClient() {
    const url = getB3ApiUrl();
    if (!restClient || restClientUrl !== url) {
        const connection = rest(url).fetch(resolveFetch());
        restClient = createClient(connection, clientOptions);
        restClientUrl = url;
    }
    return restClient;
}
/** Reset all cached clients (called when API URL changes). */
export function resetClients() {
    socketClient = null;
    socketClientUrl = null;
    restClient = null;
    restClientUrl = null;
    currentClient = null;
}
/**
 * Sets the active client type and creates the appropriate client
 *
 * Note: When using, be sure to avoid race conditions between different clients
 */
export function setClientType(t) {
    if (currentClientType === t && currentClient)
        return;
    currentClientType = t;
    currentClient = t === "socket" ? createSocketClient() : createRestClient();
}
/**
 * Gets the current active client
 */
export function getClient() {
    if (!currentClient) {
        currentClient = currentClientType === "socket" ? createSocketClient() : createRestClient();
    }
    return currentClient;
}
/**
 * Gets the current client type
 */
export function getClientType() {
    return currentClientType;
}
/**
 * Gets a specific client type (useful for parallel operations)
 */
export function getClientByType(clientType) {
    if (clientType === "socket") {
        return createSocketClient();
    }
    else {
        return createRestClient();
    }
}
/**
 * Resets the socket connection (only applicable for socket client)
 */
export function resetSocket() {
    if (socketInstance && socketInstance.connected) {
        socketInstance.disconnect();
        socketInstance.connect();
    }
}
/**
 * Authenticates with the current active client
 */
export const authenticate = async (accessToken, identityToken, params) => {
    return authenticateB3(getClient(), accessToken, identityToken, params);
};
/**
 * Authenticates with a specific client type
 */
export const authenticateWithClient = async (clientType, accessToken, identityToken, params) => {
    const client = getClientByType(clientType);
    return authenticateB3(client, accessToken, identityToken, params);
};
/**
 * Authenticates with both clients in parallel (useful for migration scenarios)
 */
export const authenticateBoth = async (accessToken, identityToken, params) => {
    const [socketResult, restResult] = await Promise.allSettled([
        authenticateWithClient("socket", accessToken, identityToken, params),
        authenticateWithClient("rest", accessToken, identityToken, params),
    ]);
    return {
        socket: socketResult.status === "fulfilled" ? socketResult.value : null,
        rest: restResult.status === "fulfilled" ? restResult.value : null,
        success: socketResult.status === "fulfilled" || restResult.status === "fulfilled",
    };
};
/**
 * Switches the client type and authenticates
 */
export async function switchClientAndAuth(type, access, id, params) {
    setClientType(type);
    return authenticateWithClient(type, access, id, params);
}
